import { useEffect, useRef, useState } from "react";
import Transition from "react-transition-group/Transition";

import { isMdDown } from "../../utils/breakpoints";

import styles from "./FieldControl.scss";

interface ErrorProps {
  text: string;
  isVisible: boolean;
  isWrappingForced?: boolean;
  forceErrorPositionAbsolute?: boolean;
}

const transitionStyles = {
  entering: { opacity: 0 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0 },
  unmounted: { opacity: 0 }
};

const Error = ({ text, isVisible, isWrappingForced, forceErrorPositionAbsolute }: ErrorProps) => {
  const [isWrapped, setIsWrapped] = useState(false);
  const errorWrapper = useRef<HTMLDivElement>(null);
  const { current: prevText } = useRef(text);
  const ifAppeared = !prevText && text;
  const ifChanged = prevText && prevText !== text;

  useEffect(() => {
    setIsTextWrapped();
    window.addEventListener("resize", setIsTextWrapped);

    return () => window.removeEventListener("resize", setIsTextWrapped);
  }, []);

  useEffect(() => {
    if (ifAppeared || ifChanged) {
      setIsTextWrapped();
    }
  }, [ifAppeared, ifChanged]);

  const setIsTextWrapped = () => {
    const errorWrapperElement = errorWrapper.current;

    if (!errorWrapperElement) {
      return;
    }

    const elementHeight = errorWrapperElement.offsetHeight;
    const elementLineHeight = parseInt(window.getComputedStyle(errorWrapperElement).lineHeight, 10);
    const isWrapped = elementHeight > elementLineHeight;

    setIsWrapped(isWrapped);
  };

  const getPosition = () => {
    // Update isMdDown if FormRow changed breakpoint for which inputs are displayed under each other
    if (forceErrorPositionAbsolute && isMdDown(window.innerWidth)) {
      return "absolute";
    }

    return isWrappingForced || isWrapped ? "relative" : "absolute";
  };

  return (
    <Transition in={isVisible} timeout={300} onEntered={setIsTextWrapped} unmountOnExit>
      {state => (
        <div
          ref={errorWrapper}
          style={{
            ...transitionStyles[state],
            position: getPosition()
          }}
          className={styles.error}
        >
          {text}
        </div>
      )}
    </Transition>
  );
};

export default Error;
